import { useState } from "react";
import { Users, Mail, Trash2, Plus, Clock, Shield } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useOrgAssignments, useRemoveAssignment } from "@/hooks/useUserAssignments";
import { useOrgInvitations, useSendInvitation, useRevokeInvitation } from "@/hooks/useInvitations";
import { useToast } from "@/hooks/use-toast";

export default function Team() {
  const { activeOrgId } = useAuth();
  const { toast } = useToast();
  const [inviteEmail, setInviteEmail] = useState("");
  const [open, setOpen] = useState(false);

  const { data: assignments = [], isLoading: loadingAssign } = useOrgAssignments(activeOrgId);
  const { data: invitations = [], isLoading: loadingInvites } = useOrgInvitations(activeOrgId);
  const removeAssignment = useRemoveAssignment();
  const sendInvitation = useSendInvitation();
  const revokeInvitation = useRevokeInvitation();

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail) return;
    try {
      await sendInvitation.mutateAsync({ email: inviteEmail, role: "property_manager" });
      toast({ title: "Invitation sent", description: `Invite sent to ${inviteEmail}` });
      setInviteEmail("");
      setOpen(false);
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    }
  };

  const handleRemove = async (id: string, name: string) => {
    if (!confirm(`Remove ${name} from this organization?`)) return;
    await removeAssignment.mutateAsync(id);
    toast({ title: "Removed", description: `${name} has been removed.` });
  };

  const roleColor = (role: string) => role === "strata_admin" ? "default" : role === "property_manager" ? "secondary" : "outline";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Team</h1>
          <p className="text-sm text-muted-foreground">Manage property managers and team members</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="mr-2 h-4 w-4" />Invite Property Manager</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Invite Property Manager</DialogTitle></DialogHeader>
            <form onSubmit={handleInvite} className="space-y-4">
              <div className="space-y-2">
                <Label>Email address</Label>
                <Input type="email" placeholder="pm@example.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} required />
              </div>
              <p className="text-xs text-muted-foreground">They'll receive a link to accept the invitation. The link expires in 7 days.</p>
              <Button type="submit" className="w-full" disabled={sendInvitation.isPending}>
                {sendInvitation.isPending ? "Sending..." : "Send Invitation"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Current Team */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Users className="h-4 w-4" />Active Team Members ({assignments.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingAssign ? <p className="text-sm text-muted-foreground">Loading...</p> : assignments.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No team members assigned yet.</p>
          ) : (
            <div className="space-y-2">
              {assignments.map((a: any) => (
                <div key={a.id} className="flex items-center justify-between rounded-md border p-3">
                  <div className="flex items-center gap-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-xs font-bold">
                      {(a.profile?.full_name ?? "?").split(" ").map((n: string) => n[0]).join("")}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{a.profile?.full_name ?? "—"}</p>
                      <p className="text-xs text-muted-foreground">{a.profile?.email ?? "—"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={roleColor(a.role) as any} className="capitalize text-xs">
                      {a.role.replace("_", " ")}
                    </Badge>
                    {a.role !== "strata_admin" && (
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" aria-label={`Remove ${a.profile?.full_name ?? "member"}`} onClick={() => handleRemove(a.id, a.profile?.full_name ?? "User")}>
                        <Trash2 className="h-3 w-3" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Clock className="h-4 w-4" />Pending Invitations ({invitations.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingInvites ? <p className="text-sm text-muted-foreground">Loading...</p> : invitations.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No pending invitations.</p>
          ) : (
            <div className="space-y-2">
              {invitations.map((inv: any) => (
                <div key={inv.id} className="flex items-center justify-between rounded-md border border-dashed p-3">
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{inv.email}</p>
                      <p className="text-xs text-muted-foreground">Expires {new Date(inv.expires_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => revokeInvitation.mutate(inv.id)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
